import { Pad, PadBox } from 'component/basics';
import { ListSelector, SaveableTextField } from 'component/form';
import type { PollVote } from 'component/poll';
import { PollWidget } from 'component/poll';
import { TextField, UtilityText } from 'component/text';
import { useState } from 'react';
import { View } from 'react-native';
import type { QuestionConfig } from 'structure/question';
import { useDatastore, useGlobalParams, useGlobalProperty } from 'util/datastore';
import { sortByOrder } from 'util/sort-by-order';
import type { Feature } from 'util/stdtypes';
import { declareGlobalProperty, declareTable, t } from 'util/stdtypes';

export const PollQuestionFeature: Feature<QuestionConfig, {}> = {
    key: 'poll-question',
    name: 'Poll Question',
    dev: true,
    providesConfig: {
        pageBottomWidgets: [PollQuestion],
        editScreenWidgets: [
            {
                slot: 'prosocial-format-widget',
                component: PollQuestionEditScreenWidget,
            },
        ],
    },
};

type PollOptions = {
    question: string | null;
    options: string[];
};

const pollVotesTable = declareTable<PollVote>('pollVotes');
export const pollOptionsProperty = declareGlobalProperty<PollOptions>('pollOptions');

export function PollQuestion() {
    const pollOptions = useGlobalProperty(pollOptionsProperty);
    if (!pollOptions || !pollOptions.question || !pollOptions.options) return null;

    return (
        <PadBox top={64}>
            <PollWidget optionTexts={pollOptions.options} pollKey={pollVotesTable} question={pollOptions.question} />
        </PadBox>
    );
}

export function PollQuestionEditScreenWidget() {
    const pollOptions = useGlobalProperty(pollOptionsProperty) ?? { question: null, options: [] };
    const datastore = useDatastore();
    const question = pollOptions?.question;

    function onSaveQuestion(question: string) {
        const newPollOptions = { ...pollOptions, question };
        datastore.setGlobalProperty(pollOptionsProperty, newPollOptions);
    }

    async function onAddOptions(options: string[]) {
        const newPollOptions = { question: pollOptions?.question, options: [...(pollOptions?.options ?? []), ...options] };
        await datastore.setGlobalProperty(pollOptionsProperty, newPollOptions);
    }

    function onDeleteOption(option: string) {
        const newPollOptions = { question: pollOptions?.question, options: pollOptions?.options?.filter((o) => o !== option) ?? [] };
        datastore.setGlobalProperty(pollOptionsProperty, newPollOptions);
    }

    function onChangeSortOrder(orderedKeys: string[]) {
        datastore.setGlobalProperty(pollOptionsProperty, { question: pollOptions?.question, options: orderedKeys });
    }

    const items = pollOptions?.options?.map((option) => ({ key: option, text: option })) ?? [];

    return (
        <View>
            <SaveableTextField
                label={t('Poll question')}
                onSave={onSaveQuestion}
                placeholder={t('Write your poll question...')}
                value={question ?? null}
            />

            <Pad size={16} />

            <ListSelector
                addLabel={t('Add option')}
                addWidget={AddOption}
                items={items}
                label={t('Poll options')}
                onAddItems={onAddOptions}
                onChangeSortOrder={onChangeSortOrder}
                onDeleteItem={onDeleteOption}
                reorderable
            />
        </View>
    );
}

function AddOption({ setToAdd, onSubmit }) {
    const [value, setValue] = useState('');
    return (
        <TextField
            autoFocus={true}
            multiline={false}
            onChange={(text) => {
                setValue(text);
                setToAdd([text]);
            }}
            onSubmit={onSubmit}
            placeholderLabel={t('Write your option...')}
            value={value}
        />
    );
}
